import React, { useEffect, useState } from "react";
import { getDataFromLocalStorage } from "../util/localStorage";

const heroNodes = [
  { label: "Support AI", detail: "Customer care", accent: "#6d4aff", className: "dlx-node-support" },
  { label: "WhatsApp", detail: "Instant replies", accent: "#13a66f", className: "dlx-node-whatsapp" },
  { label: "Vision AI", detail: "Face recognition", accent: "#8b5cf6", className: "dlx-node-vision" },
  { label: "Surveillance", detail: "Space visibility", accent: "#20c997", className: "dlx-node-surveillance" },
  { label: "Handoff", detail: "Human escalation", accent: "#f2a22c", className: "dlx-node-handoff" },
];

const metrics = [
  {
    value: "24x7",
    label: "AI availability",
    text: "Customer conversations handled around the clock across every branch and location.",
  },
  {
    value: "2",
    label: "core AI services",
    text: "Independent AI services that can be deployed separately or together.",
  },
  {
    value: "One",
    label: "DocLift platform",
    text: "Unified operational platform for teams, branches, customers, and monitored spaces.",
  },
];

const services = [
  {
    eyebrow: "CUSTOMER EXPERIENCE PLATFORM",
    title: "WhatsApp-first customer operations",
    text: "Automate customer conversations, appointment bookings, service requests, FAQs, and routine enquiries while keeping your team available for important conversations.",
    accent: "#6d4aff",
    points: ["WhatsApp conversations", "Appointment management", "Service request capture", "Business knowledge answers", "Smart human handoff", "Multi-branch routing"],
    signals: ["Ask", "Answer", "Book", "Resolve"],
    visualTitle: "Conversation engine",
    visualMeta: "WhatsApp + AI handoff",
    popups: [
      { title: "AI reply", meta: "Customer question answered instantly", value: "24x7" },
      { title: "Booking request", meta: "Need captured and organized", value: "Queue" },
      { title: "Human handoff", meta: "Priority chat moved to team", value: "Live" },
    ],
  },
  {
    eyebrow: "VISION AND SECURITY PLATFORM",
    title: "Live surveillance and suspect detection",
    text: "Connect existing NVR systems and camera networks to monitor spaces, detect suspects, identify watchlist matches, and alert teams in real time.",
    accent: "#13a66f",
    points: ["NVR integration", "Multi-camera monitoring", "Suspect detection", "Watchlist matching", "Real-time alerts", "Branch-level visibility"],
    signals: ["Face", "Detect", "Alert", "Review"],
    visualTitle: "Vision command layer",
    visualMeta: "Recognition + detection",
    popups: [
      { title: "Face match", meta: "Identity signal recognized", value: "Scan" },
      { title: "Activity detected", meta: "Movement event highlighted", value: "Alert" },
      { title: "Space view", meta: "Surveillance status connected", value: "Live" },
    ],
  },
];

const industries = [
  "Healthcare clinics",
  "Dental practices",
  "Salons and beauty parlours",
  "Transport hubs",
  "Educational campuses",
  "Retail chains",
  "Security-led locations",
  "Multi-branch businesses"
];

const flowSteps = [
  {
    title: "Capture",
    text: "Receive customer conversations through WhatsApp or events from connected camera systems.",
    tag: "Message intake",
    accent: "#6d4aff",
    signal: "WhatsApp",
  },
  {
    title: "Understand",
    text: "Apply business knowledge, customer rules, and operational context automatically.",
    tag: "AI reasoning",
    accent: "#13a66f",
    signal: "Answer",
  },
  {
    title: "Detect",
    text: "Identify appointments, requests, incidents, suspicious activity, or watchlist matches.",
    tag: "Vision event",
    accent: "#f2a22c",
    signal: "Alert",
  },
  {
    title: "Act",
    text: "Route conversations, notify staff, escalate incidents, and trigger workflows instantly.",
    tag: "Team action",
    accent: "#ba4b87",
    signal: "Resolve",
  },
];

const faqs = [
  {
    question: "What services does DocLift provide?",
    answer: "DocLift provides customer support automation and surveillance intelligence that can be deployed independently or together.",
  },
  {
    question: "Does every client receive separate data?",
    answer: "Yes. Every client operates inside an isolated tenant workspace with separate knowledge, users, communication channels, and surveillance configurations.",
  },
  {
    question: "Can businesses use only one service?",
    answer: "Yes. Customers can deploy customer support, surveillance intelligence, or both depending on their operational requirements.",
  },
  {
    question: "Does DocLift replace employees?",
    answer: "No. DocLift handles repetitive work and surfaces important events while people remain in control of customer relationships and operational decisions.",
  },
];

const HomeComponent = () => {
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    setUserInfo(getDataFromLocalStorage("userInfo"));
  }, []);

  const primaryHref = userInfo ? "/my-docs" : "/login";

  const handlePointerMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty("--x", `${((event.clientX - rect.left) / rect.width) * 100}%`);
    event.currentTarget.style.setProperty("--y", `${((event.clientY - rect.top) / rect.height) * 100}%`);
  };

  return (
    <main className="dlx-home" onPointerMove={handlePointerMove}>
      <section className="dlx-hero">
        <div className="dlx-shell dlx-hero-grid">
          <div className="dlx-hero-copy">
            <p className="dlx-eyebrow">
              <span />
              AI OPERATIONS PLATFORM FOR SERVICE BUSINESSES
              <span />
            </p>
            <h1>
              Make every service business smarter with <em>DocLift AI</em>.
            </h1>
            <p className="dlx-hero-text">
              DocLift helps businesses automate WhatsApp conversations, manage appointments and service requests, and monitor physical spaces with AI-powered surveillance intelligence.
            </p>

            <div className="dlx-actions">
              <a href={primaryHref} className="dlx-button dlx-button-primary">
                {userInfo ? "Book Free Demo" : "Request Access"}
              </a>
              <a href="#what-doclift-does" className="dlx-button dlx-button-secondary">
                Explore Services
              </a>
            </div>

            <div className="dlx-proof">
              <div>
                <strong>24x7</strong>
                <span>customer support</span>
              </div>
              <div>
                <strong>AI</strong>
                <span>vision intelligence</span>
              </div>
              <div>
                <strong>One</strong>
                <span>connected platform</span>
              </div>
            </div>
          </div>

          <div className="dlx-hero-visual" aria-hidden="true">
            <div className="dlx-orbit dlx-orbit-one" />
            <div className="dlx-orbit dlx-orbit-two" />
            <div className="dlx-orbit dlx-orbit-three" />
            <div className="dlx-core">
              <span>DocLift</span>
              <strong>AI</strong>
            </div>
            {heroNodes.map((node) => (
              <div
                key={node.label}
                className={`dlx-float-node ${node.className}`}
                style={{ "--accent": node.accent }}
              >
                <i />
                <span>{node.label}</span>
                <small>{node.detail}</small>
              </div>
            ))}
            <div className="dlx-metric-card dlx-metric-top">
              <strong>99%</strong>
              <span>service consistency</span>
            </div>
            <div className="dlx-metric-card dlx-metric-bottom">
              <strong>2</strong>
              <span>AI service pillars</span>
            </div>
          </div>
        </div>
      </section>

      {/* <section className="dlx-metrics-section">
        <div className="dlx-shell dlx-metrics">
          {metrics.map((item) => (
            <article key={item.label} className="dlx-stat-card">
              <strong>{item.value}</strong>
              <span>{item.label}</span>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section> */}

      <section id="what-doclift-does" className="dlx-section dlx-services-section">
        <div className="dlx-shell">
          <div className="dlx-section-heading">
            <p>Platform Capabilities</p>
            <h2>Two operational services. <br />One business platform.</h2>
          </div>

          <div className="dlx-service-grid">
            {services.map((service, serviceIndex) => (
              <article
                key={service.title}
                className={`dlx-service-card dlx-service-card-${serviceIndex + 1}`}
                style={{ "--accent": service.accent }}
              >
                <div>
                  <span className="dlx-service-eyebrow">{service.eyebrow}</span>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                  <div className="dlx-point-grid">
                    {service.points.map((point) => (
                      <span key={point}>{point}</span>
                    ))}
                  </div>
                </div>

                <div className="dlx-service-stage" aria-hidden="true">
                  <div className="dlx-stage-glow" />
                  <div className="dlx-service-shape">
                    <div className="dlx-shape-topline">
                      <span>{service.visualTitle}</span>
                      <strong>Live</strong>
                    </div>

                    <div className="dlx-shape-core">
                      <i />
                      <span>DocLift</span>
                    </div>

                    <div className="dlx-popup-deck">
                      {service.popups.map((popup, index) => (
                        <div key={popup.title} className="dlx-popup-card" style={{ "--delay": `${index * 0.18}s` }}>
                          <strong>{popup.value}</strong>
                          <span>{popup.title}</span>
                          <small>{popup.meta}</small>
                        </div>
                      ))}
                    </div>

                    <div className="dlx-shape-badge">{service.visualMeta}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="dlx-section dlx-flow-section">
        <div className="dlx-shell">
          <div className="dlx-section-heading dlx-center">
            <p>Connected Service Intelligence</p>
            <h2>From customer message or camera event to business action.</h2>
          </div>
          <div className="dlx-flow">
            {flowSteps.map((step, index) => (
              <article key={step.title} style={{ "--step": index + 1, "--accent": step.accent }}>
                <div className="dlx-flow-content">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {/* <small>{step.tag}</small> */}
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="industries" className="dlx-section dlx-industries-section">
        <div className="dlx-shell dlx-industries-layout">
          <div className="dlx-section-heading">
            <p>Industry Coverage</p>
            <h2>Built for service businesses that operate in the real world.</h2>
            <span>
              DocLift adapts to organizations where conversations, appointments, visibility, and operational awareness matter every day.
            </span>
          </div>
          <div className="dlx-industry-grid">
            {industries.map((industry, index) => (
              <article key={industry} style={{ "--step": index + 1 }}>
                <span>{industry.slice(0, 2)}</span>
                <strong>{industry}</strong>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="trust" className="dlx-section dlx-trust-section">
        <div className="dlx-shell dlx-trust-card">
          <div>
            <p>Enterprise Foundation</p>
            <h2>Enterprise-grade operations with tenant isolation built in.</h2>
          </div>
          <div className="dlx-trust-list">
            <span>Separate data and configuration for every client</span>
            <span>Branch-level access control</span>
            <span>Role-based permissions</span>
            <span>Independent WhatsApp channels</span>
            <span>Independent surveillance configurations</span>
          </div>
        </div>
      </section>

      <section className="dlx-section dlx-faq-section">
        <div className="dlx-shell">
          <div className="dlx-section-heading">
            <p>Common Questions</p>
            <h2>Clear answers for business teams.</h2>
          </div>
          <div className="dlx-faq-grid">
            {faqs.map((item) => (
              <article key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="dlx-cta">
        <div className="dlx-shell dlx-cta-panel">
          <div>
            <p>Ready to modernize your service business?</p>
            <h2>Modernize customer operations and business visibility with DocLift.</h2>
          </div>
          <a href={primaryHref} className="dlx-button dlx-button-primary">
            {userInfo ? "Book Free Demo" : "Request Access"}
          </a>
        </div>
      </section>
    </main>
  );
};

export default HomeComponent;
